import { useCallback } from 'react'

import { useQuery } from '@tanstack/react-query'
import { t } from 'i18next'

import EmptyPage from '~/components/empty-page'
import ListPagination from '~/components/ListPagination'
import Loading from '~/components/Loading'
import Panel from '~/components/Panel'
import Table from '~/components/Table'
import { getAccountId, history } from '~/helpers'
import useQueryParams from '~/hooks/useQuery'
import { useAllowOffsiteBriefingCampaign } from '~/modules/retailMedia/hooks/useAllowOffsiteBriefingCampaign'
import {
  listOffsiteAdvertiserReviewKey,
  listOffsiteAdvertiserReviewService
} from '~/modules/retailMedia/services/campaign/offsite-advertiser-review'
import { Footer } from '~/pages/products/list/styles'

import { formatOffsiteAdvertiserReviewList } from './DTO'
import { schema } from './schema'

const OffsiteAdvertiserReviewTable = () => {
  const params = useQueryParams()
  const page = Number(params.get('page')) || 1
  const quantity = Number(params.get('quantity')) || 50
  // Mesma chave que o `FilterCampaignStatus` escreve na listagem principal
  const status = (params.get('campaign_status') as CampaignStatus) || undefined

  // Mesmo gate da aba: a fila herda o publisher já resolvido (rmid ou token
  // de white label) em vez de reler a URL por conta própria
  const {
    publisherId,
    isPending: isGatePending,
    canFetchQueue
  } = useAllowOffsiteBriefingCampaign()

  const { data, isPending, isError } = useQuery({
    enabled: canFetchQueue,
    queryKey: [
      listOffsiteAdvertiserReviewKey,
      // Conta na key: o QueryClient sobrevive à troca de conta. Ver o gate.
      getAccountId(),
      publisherId ?? 'home',
      page,
      quantity,
      status
    ],
    queryFn: async () => {
      const response = await listOffsiteAdvertiserReviewService({
        ...(publisherId ? { publisher_id: publisherId } : {}),
        page,
        quantity,
        status
      })

      return {
        ...response,
        data: formatOffsiteAdvertiserReviewList(response?.data)
      }
    }
  })

  const handlePagination = useCallback(
    (value: number) => {
      params.set('page', String(value))
      history.push({ search: params.toString() })
    },
    [params]
  )

  const handleItemsPerPageChange = useCallback(
    (value: { value: number }) => {
      params.set('quantity', String(value.value))
      params.set('page', '1')
      history.push({ search: params.toString() })
    },
    [params]
  )

  const loading = isGatePending || (canFetchQueue && isPending)

  // Um refetch falho mantém o `data` anterior no cache, então erro precisa
  // vencer tabela e empty em vez de somar-se a eles. Sem publisher ou sem
  // permissão não há busca, e o empty state responde pela fila vazia
  const showError = canFetchQueue && isError
  const showEmptyQueue =
    !showError && !loading && (!canFetchQueue || data?.total === 0)
  const showQueue = canFetchQueue && !showError && !!data?.total

  return (
    <>
      <Loading status={loading}>
        {t('rm:offsiteAdvertiserReview.loading')}
      </Loading>

      {showError && (
        <EmptyPage
          title={t('rm:offsiteAdvertiserReview.error')}
          subtitle={t('rm:offsiteAdvertiserReview.errorSubtitle')}
          svgPath="photos/orders-empty"
        />
      )}

      {showEmptyQueue && (
        <EmptyPage
          title={t('rm:offsiteAdvertiserReview.notFound')}
          subtitle={t('rm:offsiteAdvertiserReview.notFoundSubtitle')}
          svgPath="photos/orders-empty"
        />
      )}

      {showQueue && (
        <>
          <Panel template="shoreline" noPaddingBody isLoading={loading}>
            {/* Divisórias verticais como na fila de revisão do publisher */}
            <Table
              schema={schema({
                // Escopo resolvido pelo gate, não o `rmid` cru da URL —
                // em white label um decoy na query apontaria a revisão
                // para outro publisher. Sem `rmid` na URL o link segue
                // limpo (o gate resolve pelo token).
                rmid: params.get('rmid') ? publisherId : null
              })}
              data={data.data}
              smallerRowPadding
              sideBorder
            />
          </Panel>

          <Footer>
            <ListPagination
              total={data.total}
              label={data.total > 1 ? t('rm:Campaigns') : t('rm:Campaign')}
              currentPage={page}
              itemsPerPage={quantity}
              menuPlacement="top"
              onClickPagination={handlePagination}
              onItemsPerPageChange={handleItemsPerPageChange}
            />
          </Footer>
        </>
      )}
    </>
  )
}

export default OffsiteAdvertiserReviewTable
